import React, { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, CreditCard, Eye,
  MessageSquare, Settings, DollarSign, ScrollText,
  Star, Sparkles, Mail, Trash2, FileCode, Beaker, Clock,
  BarChart3, Activity, Lock, Server, ChevronDown, ChevronRight,
  Menu, LogOut, Shield, Database, FileSearch, Settings2,
} from "lucide-react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import AdminGlobalSearch from "./AdminGlobalSearch";
import InfraHealthBadge from "./InfraHealthBadge";

const NAV_ITEMS = [
  { to: "/admin",            label: "Dashboard",     Icon: LayoutDashboard, end: true },
  { to: "/admin/cases",      label: "Casos",         Icon: FileText,         badgeKey: "cases" },
  // Iter26 — Submenú "SECOP": base de contratos + configuración de accesos.
  {
    id: "secop-group",
    label: "SECOP",
    Icon: Database,
    children: [
      { to: "/admin/secop/contracts", label: "Contratos",     Icon: FileSearch },
      { to: "/admin/secop/health",    label: "Dataset Health", Icon: Activity },
      { to: "/admin/secop/config",    label: "Configuración", Icon: Settings2 },
    ],
  },
  { to: "/admin/users",      label: "Usuarios",      Icon: Users },
  { to: "/admin/payments",   label: "Pagos",         Icon: CreditCard,       badgeKey: "payments_pending" },
  { to: "/admin/reviews",    label: "Revisiones",    Icon: Eye,              badgeKey: "reviews_inflight" },
  { to: "/admin/requests",   label: "Solicitudes",   Icon: MessageSquare,    badgeKey: "requests_pending" },
  { to: "/admin/normativa-review", label: "Normativa (revisión)", Icon: ScrollText },
  { to: "/admin/emails",     label: "Emails",        Icon: Mail,             badgeKey: "emails_unread" },
  { to: "/admin/emails/scheduler", label: "Email Scheduler", Icon: Clock,    badgeKey: "scheduler_pending" },
  { to: "/admin/prompts",    label: "Prompts",       Icon: FileCode },
  { to: "/admin/prompts/ab-tests", label: "A/B Tests", Icon: Beaker },
  { to: "/admin/ai-stats",   label: "Estadísticas IA", Icon: BarChart3 },
  // Iter25.g — Submenú colapsable "Auditoría" con 3 hijos.
  {
    id: "audit-group",
    label: "Auditoría",
    Icon: ScrollText,
    children: [
      { to: "/admin/ai-runs",     label: "Auditoría IA",       Icon: Activity },
      { to: "/admin/audit",       label: "Logs de acciones",   Icon: ScrollText },
      { to: "/admin/infra-audit", label: "Infraestructura",    Icon: Server },
    ],
  },
  // Iter25.h — Submenú "Seguridad" con 3 hijos.
  {
    id: "security-group",
    label: "Seguridad",
    Icon: Lock,
    children: [
      { to: "/admin/auth-settings",            label: "Configuración",      Icon: Lock },
      { to: "/admin/security/prompt-injection", label: "Prompt Injection",   Icon: Shield },
      { to: "/admin/security/score",           label: "Score",              Icon: BarChart3 },
    ],
  },
  { to: "/admin/settings",   label: "Configuración", Icon: Settings },
  { to: "/admin/costs",      label: "Costos",        Icon: DollarSign },
  { to: "/admin/nps",        label: "NPS",           Icon: Star },
  { to: "/admin/landing",    label: "Landing",       Icon: Sparkles },
  { to: "/admin/maintenance", label: "Mantenimiento", Icon: Trash2 },
];

/**
 * AdminSidebar — extraído del scope de `AdminLayout` para evitar la creación
 * inestable de componentes anidados en cada render
 * (eslint: react/no-unstable-nested-components).
 */
function AdminSidebar({
  counts,
  openGroups,
  onToggleGroup,
  onLinkClick,
  onLogout,
  pathname,
}) {
  return (
    <nav className="h-full flex flex-col" data-testid="admin-sidebar">
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="font-serif text-lg text-gray-900 leading-tight">JustSapiens</div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-primary">Procure · admin</div>
      </div>
      <ul className="flex-1 py-3 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          if (item.children) {
            const isOpen = !!openGroups[item.id];
            const hasActiveChild = item.children.some((c) => pathname.startsWith(c.to));
            const { Icon, label, id, children } = item;
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => onToggleGroup(id)}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                    hasActiveChild
                      ? "bg-primary/5 text-primary border-primary font-medium"
                      : "text-gray-700 hover:bg-gray-50 border-transparent"
                  }`}
                  data-testid={`admin-nav-group-${id}`}
                  aria-expanded={isOpen}
                >
                  <Icon size={16} className="shrink-0" />
                  <span className="flex-1 text-left">{label}</span>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isOpen && (
                  <ul className="bg-gray-50/60 border-l-2 border-gray-100" data-testid={`admin-nav-submenu-${id}`}>
                    {children.map((c) => (
                      <li key={c.to}>
                        <NavLink
                          to={c.to}
                          onClick={onLinkClick}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 pl-12 pr-5 py-2 text-xs transition-colors ${
                              isActive
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-gray-600 hover:bg-gray-100"
                            }`
                          }
                          data-testid={`admin-nav-${c.to.replace("/admin/", "")}`}
                        >
                          {c.Icon && <c.Icon size={14} className="shrink-0" />}
                          <span className="flex-1">{c.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          }
          const { to, label, Icon, end, badgeKey } = item;
          return (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                onClick={onLinkClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-primary/5 text-primary border-l-2 border-primary font-medium"
                      : "text-gray-700 hover:bg-gray-50 border-l-2 border-transparent"
                  }`
                }
                data-testid={`admin-nav-${to.replace("/admin", "").replace("/", "") || "dashboard"}`}
              >
                <Icon size={16} className="shrink-0" />
                <span className="flex-1">{label}</span>
                {badgeKey === "payments_pending" && counts.payments_pending_review > 0 && (
                  <span
                    className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                    title={`${counts.payments_pending_review} pago(s) con comprobante esperando revisión`}
                    data-testid="badge-comprobante-indicator"
                  />
                )}
                {badgeKey && counts[badgeKey] > 0 && (
                  <span className="bg-primary text-primary-foreground text-[10px] px-1.5 py-[1px] rounded-sm" data-testid={`badge-${badgeKey}`}>
                    {counts[badgeKey]}
                  </span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
      <div className="border-t border-gray-200 px-5 py-3">
        <button onClick={onLogout} className="flex items-center gap-2 text-xs text-gray-500 hover:text-rose-700" data-testid="admin-logout">
          <LogOut size={14} /> Cerrar sesión
        </button>
      </div>
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ cases: 0, payments_pending: 0, reviews_inflight: 0, requests_pending: 0 });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/nav-counts");
        if (alive) setCounts(data);
      } catch (err) {
        // Nav counts no crítico — fallback a 0. Log dev only.
        if (process.env.NODE_ENV === "development") {
          console.warn("Admin nav-counts load failed:", err);
        }
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Auto-expandir grupos cuyo path activo coincida.
  const initialOpen = useMemo(() => {
    const out = {};
    NAV_ITEMS.forEach((it) => {
      if (it.children && it.children.some((c) => location.pathname.startsWith(c.to))) {
        out[it.id] = true;
      }
    });
    return out;
  }, [location.pathname]);
  const [openGroups, setOpenGroups] = useState(initialOpen);

  const toggleGroup = useCallback(
    (id) => setOpenGroups((g) => ({ ...g, [id]: !g[id] })),
    [],
  );

  const handleLogout = useCallback(() => { logout(); navigate("/"); }, [logout, navigate]);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-60 xl:w-64 border-r border-gray-200 bg-white flex-col sticky top-0 h-screen" data-testid="admin-sidebar-desktop">
        <AdminSidebar
          counts={counts}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          onLogout={handleLogout}
          pathname={location.pathname}
        />
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={closeMobile}>
          <div className="absolute inset-0 bg-black/40" />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <AdminSidebar
              counts={counts}
              openGroups={openGroups}
              onToggleGroup={toggleGroup}
              onLinkClick={closeMobile}
              onLogout={handleLogout}
              pathname={location.pathname}
            />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-gray-700" onClick={() => setMobileOpen(true)} data-testid="admin-mobile-toggle">
              <Menu size={20} />
            </button>
            <div className="flex-1 max-w-md">
              <AdminGlobalSearch />
            </div>
            <InfraHealthBadge />
            <div className="relative">
              <button onClick={() => setUserMenu((v) => !v)} className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary" data-testid="admin-user-menu">
                <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                  {user?.email?.[0]?.toUpperCase() || "A"}
                </div>
                <span className="hidden sm:inline">{user?.email}</span>
              </button>
              {userMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg w-48 z-50">
                  <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">{user?.email}</div>
                  <button onClick={() => { setUserMenu(false); navigate("/profile/security"); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="admin-security-menu">
                    <Shield size={14} /> Seguridad
                  </button>
                  <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100" data-testid="admin-logout-menu">
                    <LogOut size={14} /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-7xl w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
